import { DicomContour } from '../dicom/dicomstructureset';
import { StructureSet } from './structureset';

export type BodyOutlineSource = Pick<
    StructureSet,
    'dicomUid' | 'image' | 'findExternalRoi' | 'generationModel'
>;

// Ranked by independence from the deformation the mask is drawn over.
export type BodyOutlineKind =
    // Drawn on this scan and not by a model: the clinical outline.
    | 'clinical'
    // Contour+'s own segmentation of this scan, held outside the patient.
    | 'contour-plus'
    // Produced by a model onto this scan, e.g. a propagated (deformed) set.
    | 'model';

const RANK: Record<BodyOutlineKind, number> = {
    clinical: 0,
    'contour-plus': 1,
    model: 2,
};

export interface BodyOutlineCandidate {
    readonly kind: BodyOutlineKind;
    readonly structureSetUid: string;
    readonly contours: ReadonlyArray<DicomContour>;
}

function outlineOf(
    structureSet: BodyOutlineSource,
    imageUid: string,
    kind: BodyOutlineKind
): BodyOutlineCandidate | undefined {
    if (structureSet.image?.dicomUid !== imageUid) {
        return undefined;
    }
    const contours = structureSet.findExternalRoi()?.contours;
    return contours && contours.length > 0
        ? { kind, structureSetUid: structureSet.dicomUid, contours }
        : undefined;
}

// Never depends on which set is selected: the same scan always gives the same order.
export function bodyOutlineCandidates(
    patientSets: ReadonlyArray<BodyOutlineSource>,
    contourPlusSets: ReadonlyArray<BodyOutlineSource>,
    imageUid: string | undefined
): BodyOutlineCandidate[] {
    if (imageUid === undefined) {
        return [];
    }
    const candidates = [
        ...patientSets.map((set) =>
            outlineOf(set, imageUid, set.generationModel ? 'model' : 'clinical')
        ),
        ...contourPlusSets.map((set) =>
            outlineOf(set, imageUid, 'contour-plus')
        ),
    ].filter((candidate): candidate is BodyOutlineCandidate => !!candidate);
    // Plain string order, not by locale, so sessions order identically.
    return candidates.sort(
        (a, b) =>
            RANK[a.kind] - RANK[b.kind] ||
            (a.structureSetUid < b.structureSetUid
                ? -1
                : a.structureSetUid > b.structureSetUid
                  ? 1
                  : 0)
    );
}

export type BodyMaskBasis =
    | BodyOutlineKind
    // Segmented from the scan itself, as the Body tool would.
    | 'generated'
    // The scan's own body is still being segmented: nothing is drawn yet.
    | 'computing'
    | 'none';
